/**
 * The catalogue.
 *
 * Browsed the way a game's outfit picker is: a category rail, a filter for what
 * ships with the service versus what you uploaded, a grid, and the colour
 * swatches for whatever's selected. One item, several swatches — same garment,
 * different colourway — which is why a swatch is a row and not a colour field.
 *
 * The same component serves the Library screen and the studio's add-input
 * picker; only the density changes, so there's one thing to learn.
 */
import { useMemo, useState } from 'react';
import type { LibraryItem, Swatch, Category } from '../lib/api';
import Img from './Img';
import './catalog.css';

const CATEGORIES: { id: Category; label: string; icon: JSX.Element }[] = [
  { id: 'outfits', label: 'Outfits', icon: <path d="M20.38 3.46 16 2a4 4 0 0 1-8 0L3.62 3.46a2 2 0 0 0-1.34 2.23l.58 3.47a1 1 0 0 0 .99.84H6v10c0 1.1.9 2 2 2h8a2 2 0 0 0 2-2V10h2.15a1 1 0 0 0 .99-.84l.58-3.47a2 2 0 0 0-1.34-2.23z" /> },
  { id: 'places', label: 'Places', icon: <><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" /><circle cx="12" cy="10" r="3" /></> },
  { id: 'objects', label: 'Objects', icon: <><path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" /><path d="m3.3 7 8.7 5 8.7-5" /><path d="M12 22V12" /></> },
  { id: 'looks', label: 'Looks', icon: <><circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M6.3 17.7l-1.4 1.4M19.1 4.9l-1.4 1.4" /></> },
];

export interface CatalogProps {
  items: LibraryItem[];
  compact?: boolean;
  /** Label on the action button, e.g. "Add as input". */
  actionLabel: string;
  onUse: (item: LibraryItem, swatch: Swatch) => void;
  /** Shown under the action button — what this will do to the prompt. */
  hint?: (item: LibraryItem) => string;
  onDelete?: (item: LibraryItem) => void;
}

export default function Catalog({ items, compact, actionLabel, onUse, hint, onDelete }: CatalogProps) {
  const [category, setCategory] = useState<Category>('outfits');
  const [source, setSource] = useState<'all' | 'included' | 'mine'>('all');
  const [sub, setSub] = useState('All');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [swatchIndex, setSwatchIndex] = useState(0);

  const inCategory = useMemo(
    () => items.filter((i) => i.category === category && (source === 'all' || i.source === source)),
    [items, category, source],
  );

  const subs = useMemo(() => {
    const seen = new Set<string>();
    inCategory.forEach((i) => { if (i.subcategory) seen.add(i.subcategory); });
    return ['All', ...Array.from(seen).sort()];
  }, [inCategory]);

  const visible = useMemo(
    () => inCategory.filter((i) => sub === 'All' || i.subcategory === sub),
    [inCategory, sub],
  );

  const selected = visible.find((i) => i.id === selectedId) ?? visible[0];
  const swatch = selected?.swatches[Math.min(swatchIndex, selected.swatches.length - 1)];

  function pickCategory(next: Category) {
    setCategory(next); setSub('All'); setSelectedId(null); setSwatchIndex(0);
  }

  return (
    <div className={`cat${compact ? ' compact' : ''}`}>
      <div className="cat-rail">
        {CATEGORIES.map((c) => {
          const n = items.filter((i) => i.category === c.id && (source === 'all' || i.source === source)).length;
          return (
            <button key={c.id} className="cat-btn" aria-pressed={c.id === category} onClick={() => pickCategory(c.id)}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                {c.icon}
              </svg>
              <span>{c.label}</span>
              <span className="n">{n}</span>
            </button>
          );
        })}
      </div>

      <div className="cat-main">
        <div className="cat-filters">
          <div className="seg">
            {(['all', 'included', 'mine'] as const).map((s) => (
              <button key={s} aria-pressed={source === s} onClick={() => { setSource(s); setSelectedId(null); }}>
                {s === 'all' ? 'All' : s === 'included' ? 'Included' : 'Mine'}
              </button>
            ))}
          </div>
          <div className="cat-subs">
            {subs.map((s) => (
              <button key={s} className="chip" aria-pressed={sub === s} onClick={() => { setSub(s); setSelectedId(null); }}>{s}</button>
            ))}
          </div>
          <span className="mono faint" style={{ marginLeft: 'auto', fontSize: 10.5 }}>{visible.length} items</span>
        </div>

        <div className="cat-grid">
          {visible.length === 0 && (
            <div className="cat-empty">
              Nothing here yet — upload something, or switch the filter back to All.
            </div>
          )}
          {visible.map((item) => (
            <button
              key={item.id}
              className="cat-tile"
              aria-pressed={item.id === selected?.id}
              onClick={() => { setSelectedId(item.id); setSwatchIndex(0); }}
            >
              {item.source === 'mine' && <span className="mine-badge">mine</span>}
              <Img src={item.swatches[0]?.src} alt={item.name} style={{ width: '100%', aspectRatio: '1', borderRadius: 0 }} />
              <span className="b">
                <span className="nm">{item.name}</span>
                {item.swatches.length > 1 && (
                  <span className="dots">
                    {item.swatches.map((s) => <i key={s.id} />)}
                  </span>
                )}
              </span>
            </button>
          ))}
        </div>
      </div>

      <div className="cat-detail">
        <span className="eyebrow">Selected</span>
        {!selected || !swatch ? (
          <div className="faint" style={{ fontSize: 12 }}>Nothing selected.</div>
        ) : (
          <>
            <Img src={swatch.src} alt={selected.name} style={{ width: '100%', aspectRatio: '1' }} />
            <div>
              <div style={{ fontSize: 13 }}>{selected.name}</div>
              <div className="mono faint" style={{ fontSize: 10, marginTop: 3 }}>
                {(selected.subcategory || selected.category).toLowerCase()} · {selected.source === 'mine' ? 'yours' : 'included'}
                {selected.usedCount > 0 && ` · used ${selected.usedCount}×`}
              </div>
            </div>
            {selected.swatches.length > 1 && (
              <>
                <span className="eyebrow">Colours</span>
                <div className="swatches">
                  {selected.swatches.map((s, i) => (
                    <button
                      key={s.id}
                      className="swatch"
                      aria-pressed={i === Math.min(swatchIndex, selected.swatches.length - 1)}
                      title={s.name || `Variant ${i + 1}`}
                      onClick={() => setSwatchIndex(i)}
                    >
                      <Img src={s.src} alt={s.name} style={{ width: '100%', height: '100%', borderRadius: 5 }} />
                    </button>
                  ))}
                </div>
                {swatch.name && <div className="mono faint" style={{ fontSize: 10.5 }}>{swatch.name}</div>}
              </>
            )}
            <button
              className="btn btn-primary btn-sm"
              style={{ marginTop: 'auto' }}
              disabled={!swatch.ready}
              onClick={() => onUse(selected, swatch)}
            >
              {swatch.ready ? actionLabel : 'Still preparing…'}
            </button>
            {hint && <div className="faint" style={{ fontSize: 11, lineHeight: 1.5 }}>{hint(selected)}</div>}
            {onDelete && selected.source === 'mine' && (
              <button className="btn-ghost faint" style={{ fontSize: 11 }} onClick={() => onDelete(selected)}>
                Delete this item
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
